import { VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import { usePopper } from 'react-popper';
import { Button, Float, Menu, Prompt, SelectItemAdapter } from '.';
import { promptName } from '../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../Legacy';
import { PopoverContext } from '../app/canvas/ui/Popover';
import { PopperInner, PopperMount } from '../app/canvas/ui/PopperElements';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { Icon, NoIcon } from '../concepts/resources/Icon';
import { BetterDropButton } from './DropButton';

// const Button = styled.div<{ active?: boolean, disabled?: boolean }>`
//     flex: 1 1 auto;
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     padding: .5rem .75rem;
//     font-weight: 400;
//     border-radius: .25rem;
//     border: solid .0625rem ${Colors.gray300};
//     color: ${Colors.text};
//     user-select: none;
//     box-shadow: 0 .0625rem .125rem 0 rgb(0 0 0 / 5%);

//     &:hover {
//         border: solid .0625rem ${darken(.1, Colors.gray300)};
//         cursor: pointer;
//     }

//     &:active {
//         border: solid .0625rem ${darken(.1, Colors.gray300)};
//         background: ${Colors.active};
//     }

//     ${p => p.active ? css`
//         border: solid .0625rem ${darken(.1, Colors.gray300)};
//     ` : ''}

//     svg:last-child {
//         margin: 0 0 0 .3rem;
//     }

//     svg:first-child {
//         margin: 0;
//     }

//     ${p => p.disabled ? css`
//         pointer-events: none;
//         opacity: 0.6;
//     ` : ''}
// `;

type MenuButtonProps = { block?: boolean, label?: string, icon?: string, disabled?: boolean, open?: boolean, setOpen?: VoidCallback1<boolean>, size?: 'sm' | 'md' | 'lg' };
export const MenuButton: LFC<MenuButtonProps> = ({ children, block, label, icon, disabled, open, setOpen, size }) => {

    const [selfOpen, setSelfOpen] = useState(false);
    if (!setOpen) {
        open = selfOpen;
        setOpen = setSelfOpen;
    }

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-start',
    });

    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);
    const pn = useMemo(() => promptName(), []);

    return <PopoverContext.Provider value={{ layers: [pn] }}>
        <Flexer inline={!block} className={`ignore-${pn}`} ref={setRefElmt} onClick={disabled ? undefined : onToggle}>
            <Button block={block} size={size} variant="frame" disabled={disabled} >
                {icon && <Icon size="1.5" src={icon} />}
                {label && <Menu.ItemTextCropped>{label}</Menu.ItemTextCropped>}
                {!icon && <Icon size="1" src="deqo://icons/hero/chevron-down" />}
            </Button>
        </Flexer>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float shadow="lg" inline>{children}</Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </PopoverContext.Provider>;
};

export const DropMenuButton: LFC<MenuButtonProps> = ({ label, icon, children, open, setOpen }) => {

    const [selfOpen, setSelfOpen] = useState(false);
    if (!setOpen) {
        open = selfOpen;
        setOpen = setSelfOpen;
    }

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-end',
    });

    const onToggle = useCallback(() => setOpen(!open), [open, setOpen]);
    const pn = promptName();

    return <>
        <BetterDropButton className={`ignore-${pn}`} active={open} ref={setRefElmt} onClick={onToggle}>
            {icon && <Icon size="1.5" src={icon} />}
            {label && <span>{label}</span>}
            {!icon && <Icon size="1" src="deqo://icons/hero/chevron-down" />}
        </BetterDropButton>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float shadow="lg" inline>{children}</Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </>;
};

const defaultSelectItemAdapter: SelectItemAdapter<any> = {
    key(item) {
        if ('key' in item) return item.name;
        if ('id' in item) return item.id;
        if ('value' in item) return item.value;
        return null;
    },
    label(item) {
        if ('label' in item) return item.label;
        if ('name' in item) return item.name;
        if ('text' in item) return item.text;
        return item.toString();
    }
};

export type MenuSelectProps<T> = {
    adapter?: SelectItemAdapter<T>,
    items: T[],
    selected?: T,
    disabled?: boolean,
    tabIndex?: number,
    onChange: VoidCallback1<T>,
};

export const MenuSelect = <T extends unknown>({ adapter, items, selected, disabled, tabIndex, onChange }: MenuSelectProps<T>) => {

    adapter = adapter ?? defaultSelectItemAdapter;

    const [open, setOpen] = useState(false);
    const label = selected ? adapter.label(selected) : '';

    return <MenuButton {...{ disabled, tabIndex, label, open, setOpen }}>
        <Menu>
            {items.map((x, i) => x === '' ? (
                <Menu.Divide key={`d${i}`} />
            ) : (
                <Menu.Item key={adapter.key(x)} onClick={() => onChange(x)}>
                    {selected === x ? <Icon size="1" src="deqo://icons/hero/check" /> : <NoIcon />}
                    {adapter.label(x)}
                </Menu.Item>
            ))}
        </Menu>
    </MenuButton>;
};
